import { cn } from "~/lib/utils";

/**
 * The colour every sidebar row draws on, whatever shape it is.
 *
 * Ported from t3code's `rowSurfaceClassName`. Status belongs to a row's
 * *content*; the surface is reserved for interaction — hover, multi-select,
 * route. Two renderers that differ only in how they lay their content out must
 * not also differ in what a hover looks like.
 *
 * They did. Activity rows used these `sidebar-row-*` tokens while the
 * project-grouped rows resolved their own palette out of `bg-primary/22`,
 * `bg-accent/85` and `dark:bg-primary/30` — a second colour system inside one
 * sidebar, and a hand-mixed tint where the app already carries the token.
 *
 * Kept in a leaf module so both `Sidebar.logic` and `activityThreadCard.logic`
 * can share it without importing each other.
 */
export function resolveSidebarRowSurfaceTone(input: {
  readonly isActive: boolean;
  readonly isSelected: boolean;
  /** Quiet rows (settled, seen) dim their text but keep their hover. */
  readonly shouldRecede?: boolean | undefined;
}): string {
  return cn(
    // Where you *are* outranks what you have ticked. Blending the two into a
    // fourth colour is what produced the hand-mixed values this replaces.
    input.isActive
      ? "bg-sidebar-row-active text-sidebar-foreground"
      : input.isSelected
        ? "bg-sidebar-row-selected text-sidebar-foreground"
        : input.shouldRecede
          ? "text-sidebar-muted-foreground/75 hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
          : "bg-transparent text-sidebar-foreground hover:bg-sidebar-row-hover",
  );
}
